import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';

import { LocationsUtilsService } from '../services/locations-utils/locations-utils.service';
import { RedirectionService } from '../services/redirection/redirection.service';

export const locationPagesGuard: CanActivateFn = (next) => {
  let isLocationsPage = false;

  if (next.url[0]) {
    isLocationsPage = next.url[0]?.path === 'locations';
  }

  if (!isLocationsPage) {
    return true;
  }

  const locationsUtilsService = inject(LocationsUtilsService);
  const redirectionService = inject(RedirectionService);
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);

  let stateLocale = '';
  let cityLocale = '';
  let postalCode = '';

  if (next.url.length > 3) {
    stateLocale = next?.url[1]?.path;
    cityLocale = next?.url[2]?.path;
    postalCode = next?.url[3]?.path;
  } else if (next.url.length > 2) {
    stateLocale = next?.url[1]?.path;
    cityLocale = next?.url[2]?.path;
  }

  const exclusions = [
    '-rental',
    '-dumpster',
    '-commercial',
    '-trash',
    '-recycling',
  ];
  // check for false positives
  if (
    exclusions.some((exclusion) =>
      next.url.some((url) => url.path.includes(exclusion))
    )
  ) {
    return true;
  }

  // Check if postalCode is actually a number
  // If it isn't, then the url structure doesn't need a redirect
  if (next.url.length > 3) {
    if (!postalCode || (postalCode && isNaN(+postalCode))) {
      return true;
    }
  }

  const formatUrlToRedirect = (
    state: string,
    city: string
  ): Observable<boolean | UrlTree> =>
    locationsUtilsService.formatUrlByCityAndState(state, city).pipe(
      map<string, boolean | UrlTree>((url: string) => {
        if (redirectionService.hasRedirected) {
          return true;
        } else {
          redirectionService.hasRedirected = true;
          // if platformServer
          // inject response, order redirect with 301
          if (isPlatformServer(platformId)) {
            redirectionService.serverRedirect(url);
          } else {
            router.navigateByUrl(url);
          }

          return false;
        }
      })
    );

  if (stateLocale && cityLocale) {
    // Check if cityLocale does not contain '-trash-pickup-and-recycling'
    if (!cityLocale.includes('-trash-pickup-and-recycling')) {
      return formatUrlToRedirect(stateLocale, cityLocale);
    }
  }

  const localCmsDetails = locationsUtilsService.getLocalPageCmsPath(postalCode);

  if (localCmsDetails) {
    return formatUrlToRedirect(localCmsDetails.state, localCmsDetails.city);
  }

  return true;
};
